import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { outpostStyles, FadingVideo, BlurText, OutpostNavbar } from '../components/OutpostShared';

const FAQItem = ({ q, a }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-black/10">
      <button 
        onClick={() => setIsOpen(!isOpen)} 
        className="w-full flex justify-between items-center py-6 text-left focus:outline-none group"
      >
        <span className="font-heading italic text-2xl md:text-3xl text-black pr-8 group-hover:text-black/70 transition-colors">{q}</span>
        <span className="text-black/40 flex-shrink-0 bg-black/5 rounded-full p-2 group-hover:bg-black/10 transition-colors">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={`w-5 h-5 transition-transform duration-300 ${isOpen ? 'rotate-45' : ''}`}>
            <path d="M12 5v14M5 12h14" />
          </svg>
        </span>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }} 
            animate={{ height: 'auto', opacity: 1 }} 
            exit={{ height: 0, opacity: 0 }} 
            className="overflow-hidden"
          >
            <p className="pb-8 text-black/70 font-body text-lg leading-relaxed max-w-3xl pt-2">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function ContentProduction() {
  const allVideos = [
    "https://adilo.bigcommand.com/8f3735dd-e4c2-48b7-8b76-b6c9df936275.mp4", // 0
    "https://adilo.bigcommand.com/6b7b6dda-fc1f-4fa2-b326-8c8146a42109.mp4", // 1
    "https://adilo.bigcommand.com/6ab17830-282a-4c66-b8b7-bbd632cb7248.mp4", // 2
    "https://adilo.bigcommand.com/8cb2fb05-277d-448e-a662-a9329f8eee23.mp4", // 3
    "https://adilo.bigcommand.com/fe6419e2-4d68-444c-b0c1-985a2de68891.mp4", // 4
    "https://adilo.bigcommand.com/dcbe99fc-64c2-4453-9641-df2ec0c98fe6.mp4", // 5
    "https://adilo.bigcommand.com/b07f6205-5ea6-47d2-bd26-3c13b7daf002.mp4", // 6 - Huge Full size
    "https://adilo.bigcommand.com/a81f6fc8-6a5d-47dd-9ff7-f1d3caeb4213.mp4", // 7
    "https://adilo.bigcommand.com/fe24e7db-2988-4169-a8c3-a1ce2bd6dd3b.mp4", // 8
    "https://adilo.bigcommand.com/2921fcd5-899b-4e41-9734-47d734536d3b.mp4", // 9
    "https://adilo.bigcommand.com/bb2734fa-0982-47d6-b184-e3ee1f414910.mp4", // 10
    "https://adilo.bigcommand.com/11b1ee87-4d6d-4846-a587-33b0fd802745.mp4", // 11
    "https://adilo.bigcommand.com/96378e7d-1df2-42b0-b2eb-73c0a5b51358.mp4"  // 12
  ];

  const problemStats = [
    { stat: "75%", text: "Of online shoppers rely on product photography when deciding whether to buy." },
    { stat: "94%", text: "More views for listings with high-quality images than those with poor photography." },
    { stat: "1200%", text: "More shares for video content than text and image content combined." },
    { stat: "67%", text: "Of consumers say image quality is 'very important' in decisions—higher than reviews." }
  ];

  const capabilities = [
    { title: "Creative / Art Direction", desc: "Our creative directors define the aesthetic, mood, narrative, and visual strategy—ensuring every shoot ladders up to your business objectives." },
    { title: "E-Commerce Photography", desc: "Clean, conversion-optimized imagery for marketplaces and DTC. Hero images, lifestyle contextuals, and infographics." },
    { title: "Graphic Design", desc: "Visual content that extends your brand identity across every medium, from digital ads to print collateral." },
    { title: "Photography", desc: "Studio, location, lifestyle, flat lay, and editorial shot with professional equipment and art-directed to perfection." },
    { title: "Post Production", desc: "Color grading, retouching, compositing, motion graphics, and editorial finishing to the highest standard." },
    { title: "Video Production", desc: "Brand films, product videos, social content, and campaign spots. We handle scripting to final delivery." },
    { title: "Seasonal Campaigns", desc: "Holiday launches, product drops, and seasonal content calendars that keep your brand relevant and timely." }
  ];

  const benefits = [
    { title: "Studio-Quality Production", desc: "Every asset produced to a professional broadcast and e-commerce standard." },
    { title: "Brand-Aligned Creative", desc: "Every visual decision rooted in your brand identity, guidelines, and strategic goals." },
    { title: "Content That Converts", desc: "Visuals strategically designed to drive clicks, engagement, and sales." }
  ];

  const processSteps = [
    { step: "01", title: "Brief", desc: "We define goals, deliverables, brand standards, and timelines together." },
    { step: "02", title: "Concept", desc: "We develop moodboards, shot lists, and art direction decks for approval." },
    { step: "03", title: "Produce", desc: "We execute the shoot with professional equipment, lighting, and styling." },
    { step: "04", title: "Refine", desc: "Our post-production team edits, retouches, and color grades every asset." },
    { step: "05", title: "Deliver", desc: "Final assets are exported in every format, ready for deployment." }
  ];

  const metrics = [
    { title: "Product Photography", value: "94%", desc: "More views and up to 40% higher conversion rates." },
    { title: "Video Content", value: "97%", desc: "Increase in purchase intent and 25% reduction in returns." },
    { title: "A+ / Rich Content", value: "3-10%", desc: "Average sales increase on Amazon with enhanced visuals." },
    { title: "Brand Consistency", value: "33%", desc: "Higher brand recall driven by cohesive visual content." },
    { title: "Seasonal Campaigns", value: "2-3x", desc: "More engagement for brands that launch 4-6 weeks early." }
  ];

  const faqs = [
    { q: "What types of products do you shoot?", a: "We've produced content across a wide range of categories — from beauty and skincare to consumer electronics, food and beverage, fashion, home goods, and more. If it's a product, we know how to make it look exceptional." },
    { q: "Do you handle everything in-house or outsource?", a: "Our core creative team — directors, photographers, videographers, editors, and designers — are all in-house or vetted long-term collaborators. We maintain full creative control from concept to delivery." },
    { q: "Can you shoot content optimized for Amazon or Shopify?", a: "Absolutely. We produce content to the exact specifications required by every major platform — Amazon main images, A+ Content modules, Shopify product galleries, and social ad formats." },
    { q: "How far in advance should I book a production?", a: "We recommend booking 4–6 weeks in advance for standard productions and 8–12 weeks for large-scale seasonal campaigns. Talk to us about rush production if needed." },
    { q: "Do you provide raw files or just finished assets?", a: "We deliver fully finished, production-ready assets as standard. Raw files or source files can be included depending on your agreement — just let us know during the briefing stage." },
    { q: "How many final assets will I receive?", a: "It depends on the scope. A typical e-commerce product shoot might yield 8–15 final images per SKU. Video productions are scoped by deliverable. Everything is outlined clearly in your proposal." },
    { q: "What does pricing look like?", a: "Pricing varies based on production scale, SKUs, content types, and deliverables. We offer day-rate productions, per-SKU pricing, and retainer models." }
  ];

  return (
    <div className="outpost-page min-h-screen text-black font-body selection:bg-black/10">
      <style>{outpostStyles}</style>
      <OutpostNavbar />

      {/* SECTION 1: Hero */}
      <section className="relative min-h-screen w-full flex flex-col items-center overflow-hidden pt-32 pb-24 px-8 md:px-16 lg:px-20">
        <FadingVideo 
          src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260418_094631_d30ab262-45ee-4b7d-99f3-5d5848c8ef13.mp4"
          className="absolute inset-0 w-full h-full object-cover z-0"
          maxOpacity={0.15}
        />

        <div className="relative z-10 w-full max-w-7xl mx-auto flex flex-col lg:flex-row gap-16 lg:gap-24 my-auto">
          <div className="w-full lg:w-1/2 flex flex-col justify-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
              className="text-sm font-body text-black/60 mb-6 uppercase tracking-widest"
            >
              // Expertise 02
            </motion.div>
            
            <BlurText 
              text="Content Production" 
              justify="flex-start"
              className="text-6xl md:text-7xl lg:text-[5.5rem] font-heading italic text-black leading-[0.85] tracking-[-3px] mb-8 text-left"
            />
            
            <motion.p 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 0.8 }}
              className="text-lg text-black/80 font-body font-light leading-relaxed max-w-xl mb-10"
            >
              Visuals that captivate and convert. We produce high-end photography, dynamic video, and cinematic campaigns that elevate your product, immerse your audience, and turn scroll-stopping creative into measurable business results.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.8 }}
            >
              <Link to="/contact" className="bg-black text-white rounded-full px-7 py-3.5 font-body font-medium flex items-center gap-2 hover:bg-black/80 transition-colors w-fit shadow-lg shadow-black/10">
                Start Your Next Production
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 ml-1"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
              </Link>
            </motion.div>
          </div>

          <div className="w-full lg:w-1/2 relative mt-12 lg:mt-0 flex items-center">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.6, duration: 1 }}
              className="relative w-full aspect-video rounded-3xl overflow-hidden shadow-2xl shadow-black/10 border border-black/5"
            >
              <video src={allVideos[0]} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 2: Problem Statement */}
      <section className="relative w-full py-24 md:py-32 px-8 md:px-16 lg:px-20 bg-black/[0.02] border-y border-black/[0.05]">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="mb-16 max-w-3xl"
          >
            <h2 className="font-heading italic text-4xl md:text-5xl text-black mb-6">Why Most Product Content Underperforms</h2>
            <p className="text-black/80 font-body text-lg leading-relaxed font-semibold">
              In a visual-first economy, your content isn't just marketing — it's your product's first impression, your brand's credibility, and your revenue's foundation. We make every frame count.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {problemStats.map((stat, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="liquid-glass rounded-2xl p-8 flex flex-col border border-black/5 hover:border-black/10 transition-colors"
              >
                <span className="font-heading italic text-5xl text-black mb-4">{stat.stat}</span>
                <p className="text-black/70 font-body text-sm leading-relaxed">{stat.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3: Massive Showreel / Portfolio */}
      <section className="relative w-full py-24 md:py-32 px-4 md:px-8 bg-black">
        <div className="max-w-7xl mx-auto flex flex-col items-center text-center mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="font-heading italic text-5xl md:text-7xl text-white mb-6"
          >
            See the Work
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
            className="text-white/70 font-body text-xl max-w-4xl"
          >
            From studio-lit e-commerce flats to location-based lifestyle campaigns, from 15-second social cuts to full cinematic brand films — every frame we produce is crafted with obsessive attention to detail, guided by creative direction rooted in strategy.
          </motion.p>
        </div>
        
        {/* Full Size Hero Video */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.2, duration: 0.8 }}
          className="w-full max-w-[100rem] mx-auto rounded-3xl overflow-hidden aspect-video relative shadow-[0_0_50px_rgba(0,0,0,0.5)] mb-8 border border-white/10"
        >
          <video src={allVideos[6]} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
        </motion.div>

        {/* Grid of Large Videos */}
        <div className="w-full max-w-[100rem] mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }} className="rounded-3xl overflow-hidden aspect-video relative shadow-2xl border border-white/10">
               <video src={allVideos[7]} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.4 }} className="rounded-3xl overflow-hidden aspect-video relative shadow-2xl border border-white/10">
               <video src={allVideos[8]} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.5 }} className="rounded-3xl overflow-hidden aspect-video relative shadow-2xl border border-white/10">
               <video src={allVideos[9]} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.6 }} className="rounded-3xl overflow-hidden aspect-video relative shadow-2xl border border-white/10">
               <video src={allVideos[11]} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
            </motion.div>
        </div>
      </section>

      {/* SECTION 4: Core Capabilities */}
      <section className="relative w-full py-24 md:py-32 px-8 md:px-16 lg:px-20 bg-black/[0.02] border-y border-black/[0.05]">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-16">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="w-full lg:w-1/3 flex flex-col">
            <h2 className="font-heading italic text-4xl md:text-5xl text-black mb-6">Core Capabilities</h2>
            <p className="text-black/70 font-body text-lg mb-10">
              Every production starts with a vision. We execute across all formats.
            </p>
            <div className="w-full aspect-[4/3] rounded-3xl overflow-hidden relative shadow-xl border border-black/5">
              <video src={allVideos[2]} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
            </div>
          </motion.div>
          
          <div className="w-full lg:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {capabilities.map((cap, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="liquid-glass rounded-2xl p-8 border border-black/5 hover:border-black/10 transition-colors"
              >
                <h4 className="font-body font-semibold text-black text-lg mb-3">{cap.title}</h4>
                <p className="text-black/70 font-body text-sm leading-relaxed">{cap.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5: Benefits */}
      <section className="relative w-full py-24 md:py-32 px-8 md:px-16 lg:px-20">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="w-full lg:w-1/2">
            <h2 className="font-heading italic text-4xl md:text-5xl text-black mb-12">What You Can Expect</h2>
            <div className="flex flex-col gap-10">
              {benefits.map((ben, i) => (
                <div key={i} className="flex gap-5 items-start">
                  <div className="w-10 h-10 rounded-full bg-black/5 flex items-center justify-center shrink-0 mt-1 border border-black/5">
                    <svg className="w-5 h-5 text-black/70" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7"></path></svg>
                  </div>
                  <div>
                    <h4 className="font-body font-semibold text-black text-xl mb-2">{ben.title}</h4>
                    <p className="text-black/70 font-body text-base leading-relaxed">{ben.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="w-full lg:w-1/2">
             <div className="rounded-3xl overflow-hidden aspect-video relative shadow-2xl border border-black/5">
                <video src={allVideos[1]} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
             </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 6: Content Types (Bento Grid) */}
      <section className="relative w-full py-24 md:py-32 px-8 md:px-16 lg:px-20 bg-black/[0.02] border-y border-black/[0.05]">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="mb-16 text-center"
          >
            <h2 className="font-heading italic text-4xl md:text-5xl text-black mb-6">What We Create</h2>
            <p className="text-black/70 font-body text-lg max-w-2xl mx-auto">
              A comprehensive suite of visual assets tailored for every touchpoint.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 auto-rows-[350px]">
            {/* BIG VIDEO */}
            <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} className="col-span-1 md:col-span-2 row-span-2 rounded-[2rem] overflow-hidden relative shadow-lg group">
              <video src={allVideos[3]} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-black/20"></div>
              <span className="absolute bottom-8 left-8 text-white font-body text-2xl uppercase tracking-widest font-medium">Brand Films</span>
            </motion.div>
            
            {/* BIG VIDEO */}
            <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="col-span-1 md:col-span-2 row-span-2 rounded-[2rem] overflow-hidden relative shadow-lg group">
              <video src={allVideos[4]} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-black/20"></div>
              <span className="absolute bottom-8 left-8 text-white font-body text-2xl uppercase tracking-widest font-medium">Social Media Video</span>
            </motion.div>
            
            {/* SMALL TEXT AND VIDEOS */}
            <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="col-span-1 md:col-span-1 row-span-1 liquid-glass rounded-[2rem] flex items-center justify-center p-6 text-center border border-black/5 hover:border-black/10 transition-colors">
              <span className="font-heading italic text-2xl text-black">Product Photography</span>
            </motion.div>
            
            <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.3 }} className="col-span-1 md:col-span-1 row-span-1 rounded-[2rem] overflow-hidden relative shadow-lg group">
               <video src={allVideos[5]} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
               <div className="absolute inset-0 bg-black/20"></div>
               <span className="absolute bottom-6 left-6 text-white font-body text-sm uppercase tracking-widest font-medium">Product Demo</span>
            </motion.div>
            
            <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.4 }} className="col-span-1 md:col-span-1 row-span-1 rounded-[2rem] overflow-hidden relative shadow-lg group">
               <video src={allVideos[12]} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
               <div className="absolute inset-0 bg-black/20"></div>
               <span className="absolute bottom-6 left-6 text-white font-body text-sm uppercase tracking-widest font-medium">Motion Graphics</span>
            </motion.div>
            
            <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.5 }} className="col-span-1 md:col-span-1 row-span-1 liquid-glass rounded-[2rem] flex items-center justify-center p-6 text-center border border-black/5 hover:border-black/10 transition-colors">
              <span className="font-heading italic text-2xl text-black">Lifestyle & Campaigns</span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 7: Process */}
      <section className="relative w-full py-24 md:py-32 px-8 md:px-16 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16 text-center">
            <h2 className="font-heading italic text-4xl md:text-5xl text-black mb-6">Our Production Process</h2>
            <p className="text-black/70 font-body text-lg max-w-2xl mx-auto">A seamless pipeline from concept to final cut.</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {processSteps.map((s, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="liquid-glass rounded-2xl p-6 flex flex-col shadow-sm border border-black/5"
              >
                <div className="flex items-center justify-between mb-8">
                  <span className="text-sm font-body uppercase tracking-widest text-black/40">Step</span>
                  <span className="text-3xl font-heading italic text-black/30">{s.step}</span>
                </div>
                <h4 className="font-body font-semibold text-black text-lg mb-3">{s.title}</h4>
                <p className="text-black/70 font-body text-sm leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 8: Numbers */}
      <section className="relative w-full py-24 md:py-32 px-8 md:px-16 lg:px-20 bg-black/[0.02] border-y border-black/[0.05]">
        <div className="max-w-7xl mx-auto flex flex-col">
          
          <div className="flex flex-col lg:flex-row gap-16 items-center mb-16">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="w-full lg:w-1/2">
              <h2 className="font-heading italic text-4xl md:text-5xl text-black mb-6">The Numbers That Matter</h2>
              <p className="text-black/70 font-body text-lg leading-relaxed">
                Every asset we produce is designed to move a metric — whether that's conversion rate, engagement, time on page, or brand recall. Beautiful alone isn't enough. It has to perform.
              </p>
            </motion.div>
            
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="w-full lg:w-1/2">
               <div className="rounded-3xl overflow-hidden aspect-[16/9] relative shadow-lg border border-black/5">
                  <video src={allVideos[10]} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
               </div>
            </motion.div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {metrics.map((metric, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-8 border border-black/5 flex flex-col shadow-sm"
              >
                <h4 className="font-body text-sm uppercase tracking-widest text-black/50 mb-2">{metric.title}</h4>
                <span className="font-heading italic text-5xl text-black mb-4">{metric.value}</span>
                <p className="text-black/70 font-body text-sm leading-relaxed">{metric.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 9: FAQ */}
      <section className="relative w-full py-24 md:py-32 px-8 md:px-16 lg:px-20">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16 text-center">
            <h2 className="font-heading italic text-4xl md:text-5xl text-black">Common Questions</h2>
          </motion.div>

          <div className="flex flex-col">
            {faqs.map((faq, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
                <FAQItem q={faq.q} a={faq.a} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 10: Final CTA */}
      <section className="relative w-full pb-32 px-8 md:px-16 lg:px-20 flex justify-center">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
          className="liquid-glass-strong rounded-[2.5rem] p-12 md:p-24 w-full max-w-6xl text-center flex flex-col items-center border border-black/10 shadow-2xl shadow-black/5 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-white/40 to-transparent pointer-events-none"></div>
          
          <h2 className="font-heading italic text-5xl md:text-7xl text-black mb-8 relative z-10">Ready to Elevate Your Visual Content?</h2>
          <p className="text-black/70 font-body max-w-3xl text-lg md:text-xl mb-12 relative z-10">
            Your product deserves to be seen at its absolute best — in every frame, on every platform, in every season. From a single product shoot to a full cinematic campaign, we bring the creative vision, technical expertise, and strategic thinking to make your brand impossible to scroll past. Let's create something extraordinary.
          </p>
          <Link to="/contact" className="bg-black text-white rounded-full px-8 py-4 font-body font-medium flex items-center gap-2 hover:bg-black/80 transition-colors shadow-xl shadow-black/20 relative z-10 text-lg">
            Book Your Creative Consultation
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 ml-1"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
          </Link>
        </motion.div>
      </section>

    </div>
  );
}